import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { stringToBool, stringToNumber } from '../../utils/ids-string-utils/ids-string-utils';
import { hoursTo12, hoursTo24, isValidDate } from '../../utils/ids-date-utils/ids-date-utils';

import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsLabelStateParentMixin from '../../mixins/ids-label-state-mixin/ids-label-state-parent-mixin';
import IdsDirtyTrackerMixin from '../../mixins/ids-dirty-tracker-mixin/ids-dirty-tracker-mixin';
import IdsFieldHeightMixin from '../../mixins/ids-field-height-mixin/ids-field-height-mixin';
import IdsLabelPositionMixin from '../../mixins/ids-label-position-mixin/ids-label-position-mixin';
import IdsColorVariantMixin from '../../mixins/ids-color-variant-mixin/ids-color-variant-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsValidationInputMixin from '../../mixins/ids-validation-mixin/ids-validation-input-mixin';
import IdsAutoSelectMixin from '../../mixins/ids-autoselect-mixin/ids-autoselect-mixin';
import IdsElement from '../../core/ids-element';

import { onPickerPopupXYSwitch } from '../ids-popup/ids-picker-popup-common';
import { getClosest } from '../../utils/ids-dom-utils/ids-dom-utils';
import {
  buildHourOptions, constructTimeString, parseTimeFromFormat, IdsTimePickerCommonAttributes, IdsTimePickerMixinAttributes
} from './ids-time-picker-common';

import '../ids-dropdown/ids-dropdown';
import '../ids-popup/ids-popup';
import './ids-time-picker-popup';
import '../ids-trigger-field/ids-trigger-field';
import type IdsTimePickerPopup from './ids-time-picker-popup';
import type IdsTriggerField from '../ids-trigger-field/ids-trigger-field';
import type IdsPopup from '../ids-popup/ids-popup';

import styles from './ids-time-picker.scss';
import IdsTriggerButton from '../ids-trigger-field/ids-trigger-button';
import { MARGIN_BLOCK_END_SIZES, MarginBlockEndKey } from '../ids-input/ids-input-attributes';

/**
 * Accessibility message keys for screen reader announcements
 * These should be translated using localeAPI.translate()
 */
const accessibilityMessageKeys = {
  pickerOpened: 'TimepickerOpened',
  pickerClosed: 'TimepickerClosed',
  pickerCancelled: 'TimepickerCancelled',
  timeSelected: 'TimepickerTimeSelected',
  timeRounded: 'TimepickerTimeRounded',
  invalidTimeFormat: 'TimepickerInvalidTimeFormat'
};

const Base = IdsAutoSelectMixin(
  IdsLabelStateParentMixin(
    IdsDirtyTrackerMixin(
      IdsFieldHeightMixin(
        IdsColorVariantMixin(
          IdsValidationInputMixin(
            IdsLabelPositionMixin(
              IdsLocaleMixin(
                IdsKeyboardMixin(
                  IdsEventsMixin(
                    IdsElement
                  )
                )
              )
            )
          )
        )
      )
    )
  )
);

/**
 * IDS TimePicker Component
 * @type {IdsTimePicker}
 * @inherits IdsElement
 * @mixes IdsLabelStateParentMixin
 * @mixes IdsFieldHeightMixin
 * @mixes IdsColorVariantMixin
 * @mixes IdsValidationInputMixin
 * @mixes IdsLocaleMixin
 * @mixes IdsKeyboardMixin
 * @mixes IdsEventsMixin
 * @mixes IdsLabelPositionMixin
 * @mixes IdsAutoSelectMixin
 * @part container - the container of the component
 * @part trigger-button - the trigger button
 * @part icon - the icon in the trigger button
 * @part input - the input element
 * @part popup - the popup with dropdowns
 * @part btn-set - the set button in the popup
 * @part hours - the hours dropdown
 * @part minutes - the minutes dropdown
 * @part seconds - the seconds dropdown
 * @part period - the period dropdown
 */
@customElement('ids-time-picker')
@scss(styles)
export default class IdsTimePicker extends Base {
  isFormComponent = true;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute(attributes.OVERFLOW)) {
      this.overflow = this.getAttribute(attributes.OVERFLOW);
    }
    if (this.hasAttribute(attributes.TOOLTIP)) {
      this.tooltip = this.getAttribute(attributes.TOOLTIP);
    }
    if (this.hasAttribute(attributes.MARGIN_BLOCK_END)) {
      this.marginBlockEnd = this.getAttribute(attributes.MARGIN_BLOCK_END) as MarginBlockEndKey;
    }
  }

  mountedCallback(): void {
    this.#configurePickerPopup();
    this.#attachEventHandlers();
    this.#attachKeyboardListeners();
    this.#applyMask();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @private
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      ...IdsTimePickerCommonAttributes,
      ...IdsTimePickerMixinAttributes,
    ];
  }

  /**
   * List of available color variants for this component
   * @returns {Array<string>}
   */
  colorVariants = ['alternate-formatter', 'borderless', 'in-cell'];

  /**
   * Push color variant to the trigger-field element
   * @returns {void}
   */
  onColorVariantRefresh(): void {
    if (this.input) {
      this.input.colorVariant = this.colorVariant;
    }
  }

  /**
   * Push label-state to the trigger-field element
   * @returns {void}
   */
  onLabelStateChange(): void {
    if (this.input) {
      this.input.labelState = this.labelState;
    }
  }

  /**
   * Push label-break to the container element
   * @returns {void}
   */
  onLabelBreakChange(): void {
    if (this.input) this.input.labelBreak = this.labelBreak;
  }

  /**
   * Push label-wrap to the trigger-field element
   * @returns {void}
   */
  onLabelWrapChange(): void {
    this.updateLabelWrapForCollapsedState();
  }

  /**
   * Push label-position to the trigger-field element
   * @returns {void}
   */
  onLabelPositionChange(): void {
    if (this.input) {
      this.input.labelPosition = this.labelPosition;
    }
  }

  /**
   * Push label-width to the trigger-field element
   * @returns {void}
   */
  onLabelWidthChange(): void {
    if (this.input) {
      this.input.labelWidth = this.labelWidth;
    }
  }

  /**
   * Push label-align-y to the trigger-field element
   * @returns {void}
   */
  onLabelAlignYChange(): void {
    if (this.input) {
      this.input.labelAlignY = this.labelAlignY;
    }
  }

  /**
   * Push label-alignment to the trigger-field element
   * @returns {void}
   */
  onLabelAlignmentChange(): void {
    if (this.input) {
      this.input.labelAlignment = this.labelAlignment;
    }
  }

  /**
   * Push field-height/compact to the trigger-field element
   * @param {string} val the new field height setting
   */
  onFieldHeightChange(val: string) {
    if (val) {
      const attr = val === 'compact' ? { name: 'compact', val: '' } : { name: 'field-height', val };
      this.input?.setAttribute(attr.name, attr.val);
    } else {
      this.input?.removeAttribute('compact');
      this.input?.removeAttribute('field-height');
    }
  }

  /**
   * Create the Template for the contents
   * @returns {string} HTML for the template
   */
  template(): string {
    if (this.embeddable) {
      return this.createEmbeddablePopup();
    }

    const formatDescriptionTemplate = this.localeAPI?.translate('TimepickerFormatDescription') || 'Time format: {0}';
    const formatDescription = formatDescriptionTemplate.replace('{0}', this.format);
    const descId = `${this.id ? `${this.id}-` : ''}format-description`;
    const colorVariant = this.colorVariant ? ` color-variant="${this.colorVariant}"` : '';
    const fieldHeight = this.fieldHeight ? ` field-height="${this.fieldHeight}"` : '';
    const labelState = this.labelState ? ` label-state="${this.labelState}"` : '';
    const labelPosition = this.labelPosition ? ` label-position="${this.labelPosition}"` : '';
    const labelWidth = this.labelWidth ? ` label-width="${this.labelWidth}"` : '';
    const labelAlignY = this.labelAlignY ? ` label-align-y="${this.labelAlignY}"` : '';
    const labelAlignment = this.labelAlignment ? ` label-alignment="${this.labelAlignment}"` : '';
    const labelWrap = this.labelWrap ? ` label-wrap="${this.labelWrap}"` : '';
    const compact = this.compact ? ' compact' : '';
    const noMargins = this.noMargins ? ' no-margins' : '';
    const overflow = this.overflow ? ` overflow="${this.overflow}"` : '';
    const tooltip = this.tooltip ? ` tooltip="${this.tooltip}"` : '';

    return `
      <div class="ids-time-picker" part="container">
        <ids-trigger-field
          ${this.id ? `id="${this.id}"` : ''}
          ${colorVariant}${fieldHeight}${compact}${noMargins}${labelState}${labelPosition}${labelWidth}${labelAlignY}${labelAlignment}${labelWrap}
          label="${this.label}"
          size="${this.size}"
          placeholder="${this.placeholder}"
          value="${this.value}"
          ${this.disabled ? `disabled="${this.disabled}"` : ''}
          ${this.readonly ? `readonly="${this.readonly}"` : ''}
          ${this.dirtyTracker ? `dirty-tracker="${this.dirtyTracker}"` : ''}
          ${this.validate ? `validate="${this.validate}"` : ''}
          ${this.labelBreak ? ` label-break="${this.labelBreak}"` : ''}${overflow}${tooltip}
          validation-events="${this.validationEvents}"
          part="input"
        >
          <ids-text audible="true" translate-text="true">UseArrow</ids-text>
          <ids-trigger-button
            slot="trigger-end"
            part="trigger-button"
            id="${this.id ? `${this.id}-` : ''}trigger-btn-internal"
            aria-label="${this.localeAPI?.translate('TimepickerTriggerButton') || 'Open time picker'}"
          >
            <ids-text audible="true" translate-text="true">TimepickerTriggerButton</ids-text>
            <ids-icon icon="clock" part="icon"></ids-icon>
          </ids-trigger-button>
          <span id="${this.id ? `${this.id}-description` : 'time-picker-description'}" ${htmlAttributes.HIDDEN}>
          ${this.localeAPI?.translate('TimepickerInstructions') || 'Press enter or down arrow to open time picker. Use arrow keys to select hours, minutes, and period.'}
          </span>
        </ids-trigger-field>
        <span id="${descId}" class="ids-time-picker-format-description" hidden>${formatDescription}</span>
      </div>
    `;
  }

  /**
   * Create the popup element HTML for embeddable mode
   * @private
   * @returns {string} HTML for the popup
   */
  private createEmbeddablePopup(): string {
    return `<ids-time-picker-popup
        id="${this.id ? `${this.id}-` : ''}popup-internal"
        align="left, top"
        animated
        arrow="bottom"
        autoupdate="${this.autoupdate}"
        embeddable="${this.embeddable}"
        format="${this.format}"
        trigger-type="click"
        value="${this.value}"
      ></ids-time-picker-popup>`;
  }

  /**
   * Create the popup element dynamically
   * @private
   */
  #createPickerPopup() {
    const popup = document.createElement('ids-time-picker-popup');
    popup.id = `${this.id ? `${this.id}-` : ''}popup-internal`;
    popup.setAttribute('align', 'left, top');
    popup.setAttribute('animated', '');
    popup.setAttribute('arrow', 'bottom');
    popup.setAttribute('autoupdate', String(this.autoupdate));
    popup.setAttribute('embeddable', String(this.embeddable));
    popup.setAttribute('format', this.format);
    popup.setAttribute('minute-interval', String(this.minuteInterval));
    popup.setAttribute('second-interval', String(this.secondInterval));
    popup.setAttribute('start-hour', String(this.startHour));
    popup.setAttribute('end-hour', String(this.endHour));
    popup.setAttribute('trigger-type', 'click');
    popup.setAttribute('use-current-time', this.useCurrentTime ? 'true' : 'false');
    this.#copyTimeAttributesToPopup(popup);
    popup.setAttribute('value', this.value);

    this.container?.appendChild(popup);
  }

  /**
   * Copy time attributes (hours, minutes, seconds, period) to popup if they exist
   * @private
   * @param {HTMLElement} popup - The popup element to copy attributes to
   */
  #copyTimeAttributesToPopup(popup: HTMLElement) {
    const timeAttrs = [attributes.HOURS, attributes.MINUTES, attributes.SECONDS, attributes.PERIOD];
    timeAttrs.forEach((attr) => {
      if (this.hasAttribute(attr)) popup.setAttribute(attr, this.getAttribute(attr)!);
    });
  }

  /**
   * Check if any time attributes are set
   * @private
   * @returns {boolean} true if any time attribute is set
   */
  #hasTimeAttributes(): boolean {
    return this.hasAttribute(attributes.HOURS)
      || this.hasAttribute(attributes.MINUTES)
      || this.hasAttribute(attributes.SECONDS)
      || this.hasAttribute(attributes.PERIOD);
  }

  /**
   * Add picker popup if not loaded yet due to deferred loading
   */
  createPickerPopup() {
    if (!this.picker) {
      this.#createPickerPopup();
      this.#configurePickerPopup();
      this.#attachPickerEventHandlers();
    }
  }

  /**
   * Callback for dirty tracker setting change
   * @param {boolean} value The changed value
   * @returns {void}
   */
  onDirtyTrackerChange(value: boolean) {
    if (value) {
      this.input?.setAttribute(attributes.DIRTY_TRACKER, String(value));
    } else {
      this.input?.removeAttribute(attributes.DIRTY_TRACKER);
    }
  }

  /**
   * Configure the time picker popup with target, trigger, and event handlers
   * @private
   */
  #configurePickerPopup() {
    const btn = this.triggerButton;
    const btnId = btn?.getAttribute('id');
    const pickerId = this.picker?.getAttribute('id');

    if (this.picker) {
      this.picker.format = this.format;

      if (this.input) {
        const nativeInput = this.input.input;
        if (nativeInput) {
          const descId = `${this.id ? `${this.id}-` : ''}format-description`;
          const instructionsId = `${this.id ? `${this.id}-description` : 'time-picker-description'}`;
          nativeInput.setAttribute(htmlAttributes.ROLE, 'combobox');
          nativeInput.setAttribute(htmlAttributes.ARIA_HASPOPUP, 'dialog');
          nativeInput.setAttribute(htmlAttributes.ARIA_EXPANDED, 'false');
          nativeInput.setAttribute(htmlAttributes.ARIA_LABELLED_BY, `${descId} ${instructionsId}`);
          if (pickerId) {
            nativeInput.setAttribute(htmlAttributes.ARIA_CONTROLS, pickerId);
          }
        }
        this.setAttribute(htmlAttributes.ARIA_EXPANDED, 'false');
        const inputValue = this.input.value;
        if (inputValue) {
          this.picker.value = inputValue;
        } else if (!this.#hasTimeAttributes()) {
          // Only set current time when value is empty AND no time attributes are set
          const currentTime = this.#getCurrentTimeRounded();
          this.picker.value = this.useCurrentTime ? currentTime : '';
        }
      }

      if (btn) {
        this.picker.setAttribute(attributes.TARGET, `#${btnId}`);
        this.picker.setAttribute(attributes.TRIGGER_TYPE, 'click');
        this.picker.setAttribute(attributes.TRIGGER_ELEM, `#${btnId}`);

        if (this.picker.popup) {
          this.picker.popup.x = 0;
          this.picker.popup.setAttribute(attributes.ARROW_TARGET, `#${btnId}`);
          this.picker.popup.onXYSwitch = onPickerPopupXYSwitch;

          // Adjust popup position when flipped (opens above the field)
          this.picker.popup.onPlaceEnd = (popupRect: DOMRect, domElement: HTMLElement) => {
            const arrowEl = (domElement as unknown as { arrowEl: HTMLElement }).arrowEl;
            const isFlipped = arrowEl?.classList?.contains('top');
            if (isFlipped) {
              domElement.style.top = `${parseInt(domElement.style.top || '0')}px`;
            }
            return popupRect;
          };

          // Configure positioning for modal/action panel contexts
          const isInModal = getClosest(this, 'ids-modal');
          const isInActionPanel = getClosest(this, 'ids-action-panel');
          if (isInModal) {
            this.picker.popup.setAttribute(attributes.POSITION_STYLE, 'fixed');
            this.picker.popup.setAttribute(attributes.OFFSET_CONTAINER, 'ids-modal');
          } else if (isInActionPanel) {
            this.picker.popup.setAttribute(attributes.POSITION_STYLE, 'fixed');
            this.picker.popup.setAttribute(attributes.OFFSET_CONTAINER, 'ids-action-panel');
          }

          this.picker.popup.onOutsideClick = async (e: Event) => {
            const path = e.composedPath();
            if (this.picker && !path?.includes(this.picker) && (!this.input || !path?.includes(this.input))) {
              this.autoSelectBlocked = true;
              await this.picker.hide();
              this.resetAutoSelectBlocked();
            }
          };
        }

        if (this.picker.refreshTriggerEvents) this.picker.refreshTriggerEvents();
      }
    }
  }

  /**
   * Establish Internal Keyboard shortcuts
   * @private
   * @returns {IdsTimePicker} this class-instance object for chaining
   */
  #attachKeyboardListeners(): IdsTimePicker {
    this.listen(['ArrowDown', 'Escape', 'Backspace'], this, async (e: KeyboardEvent) => {
      e.stopPropagation();
      if (e.key === 'ArrowDown') {
        this.createPickerPopup();
        this.picker?.show();
      } else if (e.key === 'Escape') {
        this.autoSelectBlocked = true;
        await this.picker?.hide(true);
        this.resetAutoSelectBlocked();
        this.#announceToScreenReader(this.localeAPI.translate(accessibilityMessageKeys.pickerCancelled) || 'Time picker cancelled');
      } else if (e.key === 'Backspace') {
        this.autoSelectBlocked = true;
        await this.picker?.hide(true);
        this.resetAutoSelectBlocked();
      }
    });

    this.listen(['Enter'], this.input, async (e: KeyboardEvent) => {
      if (!this.disabled && !this.readonly && !this.picker?.visible) {
        e.preventDefault();
        this.createPickerPopup();
        this.picker?.show();
      }
    });

    return this;
  }

  /**
   * Get current time rounded to intervals
   * @private
   * @returns {string} The current time rounded to intervals in the specified format
   */
  #getCurrentTimeRounded(): string {
    const now = new Date();
    let hours = now.getHours();
    let minutes = now.getMinutes();
    let seconds = now.getSeconds();

    // Clamp hours to startHour/endHour range without affecting minutes/seconds
    if (this.hasHourRange()) {
      if (hours < this.startHour) hours = this.startHour;
      else if (hours > this.endHour) hours = this.endHour;
    }

    if (this.minuteInterval) {
      minutes = this.roundPartToInterval(minutes, this.minuteInterval);
    }

    if (this.secondInterval) {
      seconds = this.roundPartToInterval(seconds, this.secondInterval);
    }

    const roundedTime = new Date(now);
    roundedTime.setHours(hours);
    roundedTime.setMinutes(minutes);
    roundedTime.setSeconds(seconds);

    return this.localeAPI.formatDate(roundedTime, { pattern: this.format });
  }

  /**
   * Rounds the current time value according to minute and second intervals
   * @private
   * @returns {string} The rounded time string in the specified format
   */
  #getRoundedTime() {
    if (!this.input?.value) {
      return '';
    }
    const timeOnField = parseTimeFromFormat(this.input!.value, this.format);

    if (this.minuteInterval) {
      const roundedMinutes = this.roundPartToInterval(timeOnField.minutes, this.minuteInterval);
      timeOnField.minutes = roundedMinutes >= 60 ? 0 : roundedMinutes;
    }

    if (this.secondInterval) {
      const roundedSeconds = this.roundPartToInterval(timeOnField.seconds, this.secondInterval);
      timeOnField.seconds = roundedSeconds >= 60 ? 0 : roundedSeconds;
    }

    return constructTimeString(timeOnField, this.format);
  }

  /**
   * Establish Internal Event Handlers
   * @returns {IdsTimePicker} this class-instance object for chaining
   * @private
   */
  #attachEventHandlers(): IdsTimePicker {
    // Trigger button click to open/close popup
    this.offEvent('click.time-trigger-button');
    this.onEvent('click.time-trigger-button', this.triggerButton, () => {
      if (this.disabled || this.readonly) return;
      this.createPickerPopup();
      this.picker?.toggleVisibility();
    });

    this.offEvent('blur.picker');
    this.onEvent('blur.picker', this, () => {
      if (this.roundToInterval) {
        const originalValue = this.value;
        const roundedValue = this.#getRoundedTime();
        this.value = roundedValue;
        if (originalValue !== roundedValue && roundedValue) {
          this.#announceToScreenReader(this.localeAPI.translate(accessibilityMessageKeys.timeRounded, { time: roundedValue }) || `Time rounded to nearest interval: ${roundedValue}`);
        }
      }

      if (typeof this.state.beforeValidate === 'function') {
        const validatedValue = this.state.beforeValidate({ api: this.picker });
        if (validatedValue !== undefined) {
          this.setAttribute(attributes.VALUE, validatedValue);
          this.#parseInputValue();
        }
      }
    });

    this.offEvent('change.time-picker-dropdowns');
    this.onEvent('change.time-picker-dropdowns', this.container?.querySelector('.dropdowns'), async (e: any) => {
      const currentId = e.detail?.elem?.id;

      if (!currentId) return;

      // Use super.setAttribute to avoid infinite recursion
      super.setAttribute(currentId, e.detail.value);

      if (this.autoupdate) {
        this.autoSelectBlocked = true;
        await this.picker?.hide();
        this.resetAutoSelectBlocked();
      }
    });

    this.offEvent('timeselected.picker');
    this.onEvent('timeselected.picker', this.container, async () => {
      const args = {
        bubbles: true,
        detail: {
          elem: this,
          date: this.picker!.activeDate,
          value: this.#getTimeOnField()
        }
      };

      const event = new CustomEvent('timeselected', args);
      this.dispatchEvent(event);

      this.#setTimeOnField();

      const selectedTime = this.#getTimeOnField();
      this.#announceToScreenReader(this.localeAPI.translate(accessibilityMessageKeys.timeSelected, { time: selectedTime }) || `Time selected: ${selectedTime}`);

      if (this.autoopen) {
        this.autoSelectBlocked = true;
        this.picker?.addEventListener('hide', () => {
          this.resetAutoSelectBlocked();
        }, { once: true });
      }

      await this.picker?.hide();
    });

    this.offEvent('focus.time-picker-input');
    this.onEvent('focus.time-picker-input', this.input, () => {
      this.createPickerPopup();
      if (this.picker && this.autoopen && !this.picker.visible && !this.autoSelectBlocked) {
        this.picker.show();
      }
    });

    this.offEvent('input.time-picker-input');
    this.onEvent('input.time-picker-input', this.input, (e: any) => {
      this.#triggerInputEvent(e.detail?.value || e.target?.value);
      this.#parseInputValue();
    });

    return this;
  }

  /**
   * Attach event handlers specifically for the picker popup
   * @private
   */
  #attachPickerEventHandlers(): void {
    if (!this.picker) return;

    this.offEvent('hide.picker');
    this.onEvent('hide.picker', this.picker, (e: CustomEvent) => {
      if (e.detail.doFocus && !this.autoopen) {
        this.input?.focus();
      }
      this.onHide();
    });

    this.offEvent('show.picker');
    this.onEvent('show.picker', this.picker, () => {
      this.onShow();
    });
  }

  /**
   * Respond to changing locale
   */
  onLocaleChange = () => {
    if (this.picker) {
      this.picker.format = this.format;
      this.picker.locale = this.locale;
    }
    if (this.input) {
      this.input.format = this.format;
      this.input.locale = this.locale;
    }
    this.#applyMask();
    this.picker?.renderDropdowns();
  };

  /**
   * Respond to changing language
   */
  onLanguageChange = () => {
    this.picker?.renderDropdowns();
  };

  private isUpdatingFromInput = false;

  private autoSelectBlocked = false;

  private resetTimeoutId: number | null = null;

  /**
   * Debounced method to reset autoSelectBlocked flag
   * @private
   */
  private resetAutoSelectBlocked(): void {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
    this.resetTimeoutId = requestAnimationFrame(() => {
      this.autoSelectBlocked = false;
      this.resetTimeoutId = null;
    });
  }

  /**
   * Parse input date and populate dropdowns
   */
  #parseInputValue(): void {
    if (this.isUpdatingFromInput) return;

    this.isUpdatingFromInput = true;

    const value = this.input?.value || this.value;
    const inputDate = this.localeAPI?.parseDate(
      value,
      { dateFormat: this.format }
    ) as Date;
    const hours24 = inputDate?.getHours();
    const hours12 = hoursTo12(hours24);
    const minutes = inputDate?.getMinutes();
    const seconds = inputDate?.getSeconds();
    const period = inputDate && this.localeAPI?.calendar()?.dayPeriods[hours24 >= 12 ? 1 : 0];

    if (this.#is24Hours() && hours24 !== this.hours) {
      this.hours = hours24 >= 0 ? hours24 : null;
    }

    if (this.is12Hours() && hours12 !== this.hours) {
      this.hours = hours12 >= 0 ? hours12 : null;
    }

    if (minutes !== this.minutes) {
      this.minutes = minutes >= 0 ? minutes : null;
    }

    if (seconds !== this.seconds) {
      this.seconds = seconds >= 0 ? seconds : null;
    }

    if (this.hasPeriod() && period !== this.period) {
      this.period = period;
    }
    this.isUpdatingFromInput = false;
  }

  /**
   * @returns {boolean} returns true if the timepicker format includes a day period ("a")
   */
  hasPeriod(): boolean {
    return this.is12Hours() && (this.format.toLowerCase().indexOf(' a') > -1 || this.format.toLowerCase().indexOf('a') === 0);
  }

  /**
   * @returns {boolean} returns true if the timepicker is using a 12-Hour format ("hh")
   */
  is12Hours(): boolean {
    return this.format.includes('h');
  }

  /**
   * @returns {boolean} returns true if the timepicker is using a 24-Hour format ("HH")
   */
  #is24Hours(): boolean {
    return this.format.includes('H') || !this.hasPeriod();
  }

  /**
   * Get options list for hours dropdown
   * @returns {Array<number>} options
   */
  getHourOptions(): Array<number> {
    return buildHourOptions({
      startHour: this.startHour,
      endHour: this.endHour,
      is12Hours: this.is12Hours(),
      is24Hours: this.#is24Hours()
    });
  }

  /**
   * @returns {boolean} true if range is set
   */
  hasHourRange(): boolean {
    return this.startHour > 0 || this.endHour < 24;
  }

  /**
   * @returns {Array<string>} list of available day periods
   */
  dayPeriodsWithRange(): Array<string> {
    const dayPeriods: Array<string> = this.localeAPI?.calendar().dayPeriods || [];

    if (!this.hasHourRange()) {
      return dayPeriods;
    }

    // Do not include out of range day period
    return dayPeriods.reduce((prev: Array<string>, curr: string, index: number) => {
      const amInRange = index === 0 && (this.startHour < 12 || this.startHour === 0);
      const pmInRange = index === 1 && this.endHour >= 12;

      if (amInRange || pmInRange) {
        return [...prev, curr];
      }

      return prev;
    }, []);
  }

  /**
   * Get the input-field's timestring value
   * @returns {string} formatted timestring
   */
  #getTimeOnField(): string {
    const date: Date = new Date();

    const dayPeriodIndex: number = this.localeAPI?.calendar().dayPeriods?.indexOf(this.period);
    date.setHours(
      hoursTo24(this.hours, dayPeriodIndex),
      this.minutes,
      this.seconds
    );

    return this.localeAPI.formatDate(date, { pattern: this.format });
  }

  /**
   * Set the input-field's timestring value
   */
  #setTimeOnField(): void {
    this.value = this.#getTimeOnField();
  }

  /**
   * Triggers an input event when user types
   * @private
   * @param {string} inputValue - the current input value
   */
  #triggerInputEvent(inputValue: string): void {
    const inputEvent = new CustomEvent('input', {
      bubbles: true,
      detail: {
        elem: this,
        value: inputValue
      }
    });
    this.dispatchEvent(inputEvent);
  }

  /**
   * Valid time validation extend validation mixin
   */
  #setTimeValidation(): void {
    if (this.validate?.includes('time') && this.input && typeof this.input.addValidationRule === 'function') {
      this.input.addValidationRule({
        id: 'time',
        type: 'error',
        message: this.localeAPI?.translate('InvalidTime'),
        messageId: 'InvalidTime',
        check: (input: any) => {
          if (!input.value) return true;

          const date = this.localeAPI.parseDate(
            input.value,
            { dateFormat: this.format, strictTime: true }
          ) as Date;

          const isValid = isValidDate(date);
          if (isValid) {
            this.input?.removeAttribute(htmlAttributes.ARIA_INVALID);
          } else {
            this.input?.setAttribute(htmlAttributes.ARIA_INVALID, 'true');
            this.#announceToScreenReader(this.localeAPI.translate(accessibilityMessageKeys.invalidTimeFormat) || 'Invalid time format. Please enter time in correct format.');
          }
          return isValid;
        }
      });
    }
  }

  /**
   * @param {number} value minutes or seconds to be rounded
   * @param {number} interval for value to be rounded to
   * @returns {number} rounded value
   */
  roundPartToInterval(value: number, interval: number): number {
    return Math.round(value / interval) * interval;
  }

  /**
   * Applying ids-mask to the input when changing locale or format
   */
  #applyMask() {
    if (this.input && this.mask) {
      this.input.mask = 'date';
      this.input.maskOptions = { format: this.format };
    }
  }

  /**
   * Public method to open timepicker popup
   * @returns {void}
   */
  open(): void {
    this.createPickerPopup();
    this.picker?.show();
  }

  /**
   * Public method to close timepicker popup
   */
  async close() {
    await this.picker?.hide(false);
  }

  /**
   * Close the timepicker's popup window
   */
  onHide() {
    this.container?.classList.remove('is-open');
    this.picker?.setAttribute(attributes.TABINDEX, '-1');
    const nativeInput = this.input?.input;
    if (nativeInput) {
      nativeInput.setAttribute(htmlAttributes.ARIA_EXPANDED, 'false');
    }
    this.setAttribute(htmlAttributes.ARIA_EXPANDED, 'false');
    this.#announceToScreenReader(this.localeAPI.translate(accessibilityMessageKeys.pickerClosed) || 'Time picker closed');
  }

  /**
   * Open the timepicker's popup window
   */
  onShow() {
    this.container?.classList.add('is-open');

    if (this.picker) {
      this.picker.removeAttribute(attributes.TABINDEX);
      const nativeInput = this.input?.input;
      if (nativeInput) {
        nativeInput.setAttribute(htmlAttributes.ARIA_EXPANDED, 'true');
      }
      if (this.picker.popup) {
        this.picker.popup.align = `bottom, ${!this.localeAPI.isRTL() && ['md', 'lg', 'full'].includes(this.size) ? 'right' : 'left'}`;
        this.picker.popup.arrow = 'bottom';
        this.#configurePickerPopup();
      }
    }
    this.setAttribute(htmlAttributes.ARIA_EXPANDED, 'true');

    // Always update dropdown values with current time when the popup is opened if no value is set
    if (!this.input?.value) {
      this.picker?.syncTimeAttributes(null);
    }

    this.picker?.focus();
    this.#announceToScreenReader(this.localeAPI.translate(accessibilityMessageKeys.pickerOpened) || 'Time picker opened. Use arrow keys to navigate time options.');
  }

  /**
   * Add announcement helper
   * @private
   * @param {string} message - Message to announce to screen readers
   */
  #announceToScreenReader(message: string): void {
    const announcer = document.createElement('div');
    announcer.setAttribute(htmlAttributes.ROLE, 'status');
    announcer.setAttribute(htmlAttributes.ARIA_LIVE, 'polite');
    announcer.setAttribute(htmlAttributes.ARIA_ATOMIC, 'true');
    announcer.className = 'ids-time-picker-announcer';
    announcer.style.cssText = 'position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden;';
    announcer.textContent = message;
    document.body.appendChild(announcer);
    requestAnimationFrame(() => announcer.remove());
  }

  /**
   * The beforeValidate callback
   * @param {Function} func The callback function
   */
  set beforeValidate(func) {
    this.state.beforeValidate = func;
  }

  get beforeValidate() { return this.state.beforeValidate; }

  /**
   * @returns {HTMLElement} reference to the IdsPopup component
   */
  get popup(): IdsPopup | null {
    return this.container?.querySelector<IdsPopup>('ids-popup') || null;
  }

  /**
   * @returns {HTMLElement} Reference to the IdsTriggerField
   */
  get input(): IdsTriggerField | null {
    return this.container?.querySelector<IdsTriggerField>('ids-trigger-field') || null;
  }

  /**
   * Sets the time format to use in the picker.
   * @param {string|null} value - a variation of "hh:mm:ss a"
   */
  set format(value: string | null) {
    if (value) {
      this.setAttribute(attributes.FORMAT, value);
      this.picker?.setAttribute(attributes.FORMAT, `${value}`);
    } else {
      this.removeAttribute(attributes.FORMAT);
      this.picker?.setAttribute(attributes.FORMAT, `${this.localeAPI?.calendar().timeFormat}`);
    }
    this.#applyMask();
  }

  /**
   * Gets the time format to use in the picker. Defaults to the current locale's time format or english ("hh:mm a")
   * @returns {string} the time format being used
   */
  get format(): string {
    return this.getAttribute(attributes.FORMAT) || this.localeAPI?.calendar().timeFormat || 'hh:mm a';
  }

  /**
   * Sets a current timestring-value of the timepickers input-field
   * @param {string | null | undefined} val - a timestring value for the input-field
   */
  set value(val: string | null | undefined) {
    const value = val ?? '';
    this.setAttribute(attributes.VALUE, value);

    if (value) {
      this.#parseInputValue();
    }

    if (this.input) {
      this.input.setAttribute(attributes.VALUE, value);
      // Trigger validation if validate attribute is present
      if (this.validate && !this.suppressValidation) {
        this.input.checkValidation?.();
      }
    }
  }

  /**
   * Gets a timestring that matches the format specified by this.format()
   * @returns {string} the current timestring value of the timepicker
   */
  get value(): string { return this.getAttribute(attributes.VALUE) || ''; }

  /**
   * Sets the autoopen attribute
   * @param {boolean} value - true or false
   */
  set autoopen(value) {
    const boolVal = stringToBool(value);

    if (boolVal) {
      this.setAttribute(attributes.AUTOOPEN, '');
    } else {
      this.removeAttribute(attributes.AUTOOPEN);
    }
  }

  /**
   * Gets the autoopen attribute
   * @returns {boolean} true if autoopen is enabled
   */
  get autoopen(): boolean {
    return stringToBool(this.getAttribute(attributes.AUTOOPEN));
  }

  /**
   * Sets the autoupdate attribute
   * @param {boolean} value - true or false
   */
  set autoupdate(value: boolean) {
    const boolVal = stringToBool(value);
    const popupBtn = this.container?.querySelector('.popup-btn');

    if (boolVal) {
      this.setAttribute(attributes.AUTOUPDATE, 'true');
      popupBtn?.setAttribute('hidden', 'true');
    } else {
      this.removeAttribute(attributes.AUTOUPDATE);
      popupBtn?.removeAttribute('hidden');
    }
  }

  /**
   * Gets the autoupdate attribute
   * @returns {boolean} true if autoopen is enabled
   */
  get autoupdate(): boolean {
    return stringToBool(this.getAttribute(attributes.AUTOUPDATE));
  }

  /**
   * Sets the disabled attribute
   * @param {boolean | string} value - true or false
   */
  set disabled(value: boolean | string) {
    const boolVal = stringToBool(value);

    if (boolVal) {
      this.setAttribute(attributes.DISABLED, 'true');
      this.input?.setAttribute(attributes.DISABLED, 'true');
      this.input?.setAttribute(htmlAttributes.ARIA_DISABLED, 'true');
      this.triggerButton?.setAttribute(htmlAttributes.ARIA_DISABLED, 'true');
      if (this.hasAttribute(attributes.READONLY)) {
        this.removeAttribute(attributes.READONLY);
        this.input?.removeAttribute(attributes.READONLY);
        this.input?.removeAttribute(htmlAttributes.ARIA_READONLY);
      }
    } else {
      this.removeAttribute(attributes.DISABLED);
      this.input?.removeAttribute(attributes.DISABLED);
      this.input?.removeAttribute(htmlAttributes.ARIA_DISABLED);
      this.triggerButton?.removeAttribute(htmlAttributes.ARIA_DISABLED);
    }
  }

  /**
   * Gets the disabled attribute
   * @returns {boolean | string} true if the timepicker is disabled
   */
  get disabled(): boolean | string {
    return stringToBool(this.getAttribute(attributes.DISABLED));
  }

  /**
   * Sets the readonly attribute
   * @param {boolean | string} value - true or false
   */
  set readonly(value: boolean | string) {
    const boolVal = stringToBool(value);

    if (boolVal) {
      this.setAttribute(attributes.READONLY, 'true');
      this.input?.setAttribute(attributes.READONLY, 'true');
      this.input?.setAttribute(htmlAttributes.ARIA_READONLY, 'true');
      if (this.hasAttribute(attributes.DISABLED)) {
        this.removeAttribute(attributes.DISABLED);
        this.input?.removeAttribute(attributes.DISABLED);
        this.input?.removeAttribute(htmlAttributes.ARIA_DISABLED);
        this.triggerButton?.removeAttribute(htmlAttributes.ARIA_DISABLED);
      }
    } else {
      this.removeAttribute(attributes.READONLY);
      this.input?.removeAttribute(attributes.READONLY);
      this.input?.removeAttribute(htmlAttributes.ARIA_READONLY);
    }
  }

  /**
   * Gets the readonly attribute
   * @returns {boolean | string} true if the timepicker is in readonly mode
   */
  get readonly(): boolean | string {
    return stringToBool(this.getAttribute(attributes.READONLY));
  }

  /**
   * Handles label attribute changes
   * @param {string} value label value
   */
  onLabelChange(value: string | null) {
    if (this.input) this.input.label = value ?? '';
  }

  /**
   * Handles id attribute changes
   * @param {string} value id value
   */
  onIdChange(value: string | null) {
    if (value) {
      this.input?.setAttribute(attributes.ID, `${value}-trigger-field`);
      if (this.picker) this.picker.id = `${value}-popup-internal`;
      if (this.triggerButton) this.triggerButton.id = `${value}-trigger-btn-internal`;
    } else {
      this.input?.removeAttribute(attributes.ID);
      this.picker?.removeAttribute(attributes.ID);
      this.triggerButton?.removeAttribute(attributes.ID);
    }
    this.#configurePickerPopup();
  }

  /**
   * Sets the placeholder attribute
   * @param {string} value - the placeholder's text
   */
  set placeholder(value: string) {
    if (value) {
      this.setAttribute(attributes.PLACEHOLDER, value);
      this.input?.setAttribute(attributes.PLACEHOLDER, value);
    } else {
      this.removeAttribute(attributes.PLACEHOLDER);
      this.input?.removeAttribute(attributes.PLACEHOLDER);
    }
  }

  /**
   * Get the placeholder attribute
   * @returns {string} default is ""
   */
  get placeholder(): string {
    return this.getAttribute(attributes.PLACEHOLDER) ?? '';
  }

  /**
   * Sets the margin-block-end spacing token on the inner .ids-time-picker container.
   * Accepts token shorthand keys (e.g. 'sm', 'lg') that map to IDS spacing CSS variables.
   * @param {MarginBlockEndKey | null} value spacing token key, or null to remove
   */
  set marginBlockEnd(value: MarginBlockEndKey | null) {
    if (value && Object.prototype.hasOwnProperty.call(MARGIN_BLOCK_END_SIZES, value)) {
      this.setAttribute(attributes.MARGIN_BLOCK_END, value);
    } else {
      this.removeAttribute(attributes.MARGIN_BLOCK_END);
      value = null;
    }
    if (this.input) this.input.marginBlockEnd = value;
  }

  /**
   * Gets the margin-block-end token key
   * @returns {MarginBlockEndKey | null} the current spacing token key, or null if not set
   */
  get marginBlockEnd(): MarginBlockEndKey | null {
    const val = this.getAttribute(attributes.MARGIN_BLOCK_END) as MarginBlockEndKey | null;
    if (val && Object.prototype.hasOwnProperty.call(MARGIN_BLOCK_END_SIZES, val)) return val;
    return null;
  }

  /**
   * Sets the no margins attribute
   * @param {boolean} value The value for no margins attribute
   */
  set noMargins(value: boolean) {
    const boolVal = stringToBool(value);

    if (boolVal) {
      this.setAttribute(attributes.NO_MARGINS, 'true');
      this.input?.setAttribute(attributes.NO_MARGINS, 'true');
    } else {
      this.removeAttribute(attributes.NO_MARGINS);
      this.input?.removeAttribute(attributes.NO_MARGINS);
    }
  }

  /**
   * no-margins attribute
   * @returns {boolean} noMargins parameter
   */
  get noMargins(): boolean {
    return stringToBool(this.getAttribute(attributes.NO_MARGINS));
  }

  /**
   * Set the time picker size
   * @param {string} value The value
   */
  set size(value: string) {
    if (value) {
      this.setAttribute(attributes.SIZE, value);
      this.container?.classList.add(value);
    } else {
      this.removeAttribute(attributes.SIZE);
      this.container?.classList.remove(value);
    }
    this.input?.setAttribute(attributes.SIZE, this.size);
  }

  /**
   * Get the size attribute
   * @returns {string} default is "sm"
   */
  get size(): string {
    return this.getAttribute(attributes.SIZE) ?? 'sm';
  }

  /**
   * Set interval in minutes dropdown
   * @param {string|number|null} val minute-interval attribute value
   */
  set minuteInterval(val: string | number | null) {
    const numberVal = stringToNumber(val);

    if (!Number.isNaN(numberVal)) {
      this.setAttribute(attributes.MINUTE_INTERVAL, String(numberVal));
    } else {
      this.removeAttribute(attributes.MINUTE_INTERVAL);
    }

    if (this.picker) this.picker.minuteInterval = val;
  }

  /**
   * minute-interval attribute, default is 5
   * @returns {number} minuteInterval value
   */
  get minuteInterval(): number {
    const numberVal = stringToNumber(this.getAttribute(attributes.MINUTE_INTERVAL));

    if (!Number.isNaN(numberVal)) {
      return numberVal;
    }

    return 5;
  }

  /**
   * Set interval in seconds dropdown
   * @param {string|number|null} val second-interval attribute value
   */
  set secondInterval(val: string | number | null) {
    const numberVal = stringToNumber(val);

    if (!Number.isNaN(numberVal)) {
      this.setAttribute(attributes.SECOND_INTERVAL, String(numberVal));
    } else {
      this.removeAttribute(attributes.SECOND_INTERVAL);
    }

    if (this.picker) this.picker.secondInterval = val;
  }

  /**
   * second-interval attribute, default is 5
   * @returns {number} secondInterval value
   */
  get secondInterval(): number {
    const numberVal = stringToNumber(this.getAttribute(attributes.SECOND_INTERVAL));

    if (!Number.isNaN(numberVal)) {
      return numberVal;
    }

    return 5;
  }

  /**
   * Set whether or not show only hours/minutes/seconds dropdowns without input
   * @param {boolean | string} val embeddable param value
   */
  set embeddable(val: boolean | string) {
    const boolVal = stringToBool(val);

    if (boolVal) {
      this.setAttribute(attributes.EMBEDDABLE, 'true');
    } else {
      this.removeAttribute(attributes.EMBEDDABLE);
    }

    if (this.picker) this.picker.embeddable = val;
  }

  /**
   * embeddable attribute
   * @returns {boolean | string} whether or not to show only hours/minutes/seconds dropdowns without input
   */
  get embeddable(): boolean | string {
    return stringToBool(this.getAttribute(attributes.EMBEDDABLE));
  }

  /**
   * Sets whether time values should be rounded to the nearest interval
   * @param {boolean | string} val - The value to set
   */
  set roundToInterval(val: boolean | string) {
    const boolVal = stringToBool(val);

    if (boolVal) {
      this.setAttribute(attributes.ROUND_TO_INTERVAL, 'true');
    } else {
      this.removeAttribute(attributes.ROUND_TO_INTERVAL);
    }
  }

  /**
   * Gets the current state of interval rounding
   * @returns {boolean | string} True if time values are being rounded to intervals
   */
  get roundToInterval(): boolean | string {
    return stringToBool(this.getAttribute(attributes.ROUND_TO_INTERVAL));
  }

  /**
   * Set hours attribute and update value in hours dropdown
   * @param {string|number|null} value hours param value
   */
  set hours(value: string | number | null) {
    if (value === null) return;

    this.setAttribute(attributes.HOURS, String(value));

    if (this.picker) this.picker.hours = value;
    if (this.autoupdate && !this.isUpdatingFromInput) this.#setTimeOnField();
  }

  /**
   * hours attribute, default is 1
   * @returns {number} hours attribute value converted to number
   */
  get hours(): number {
    if (this.picker) return this.picker.hours ?? 0;

    const numberVal = stringToNumber(this.getAttribute(attributes.HOURS));

    if (!Number.isNaN(numberVal)) {
      return numberVal;
    }

    if (this.hasHourRange()) {
      return this.getHourOptions()[0];
    }

    if (this.useCurrentTime && Number.isNaN(numberVal)) {
      const hours24Now = new Date().getHours();

      if (this.is12Hours()) {
        return hoursTo12(hours24Now);
      }

      return hours24Now;
    }

    return 1;
  }

  /**
   * Gets hours in 24 hour format
   * @returns {number} input value in 24 hours format
   */
  get hours24(): number {
    const inputDate = this.localeAPI?.parseDate(
      this.input?.value || this.value,
      { dateFormat: this.format }
    ) as Date;

    return inputDate?.getHours() || 0;
  }

  /**
   * Set minutes attribute and update value in minutes dropdown
   * @param {string|number|null} value minutes param value
   */
  set minutes(value: string | number | null) {
    if (value !== null) {
      this.setAttribute(attributes.MINUTES, String(value));
    } else {
      this.removeAttribute(attributes.MINUTES);
    }

    if (this.picker) this.picker.minutes = value;
    if (this.autoupdate && !this.isUpdatingFromInput) this.#setTimeOnField();
  }

  /**
   * minutes attribute, default is 0
   * @returns {number} minutes attribute value converted to number
   */
  get minutes(): number {
    if (this.picker && this.picker.minutes !== null) return this.picker.minutes;

    const numberVal = stringToNumber(this.getAttribute(attributes.MINUTES));

    if (!Number.isNaN(numberVal)) {
      return this.roundPartToInterval(numberVal, this.minuteInterval);
    }

    if (this.useCurrentTime && Number.isNaN(numberVal)) {
      const minutesNow = new Date().getMinutes();

      return this.roundPartToInterval(minutesNow, this.minuteInterval);
    }

    // Default
    return 0;
  }

  /**
   * Set seconds attribute and update value in seconds dropdown
   * @param {string|number|null} value seconds param value
   */
  set seconds(value: string | number | null) {
    if (value !== null) {
      this.setAttribute(attributes.SECONDS, String(value));
    } else {
      this.removeAttribute(attributes.SECONDS);
    }
    if (this.picker) this.picker.seconds = value;
    if (this.autoupdate && !this.isUpdatingFromInput) this.#setTimeOnField();
  }

  /**
   * seconds attribute, default is 0
   * @returns {number} seconds attribute value converted to number
   */
  get seconds(): number {
    if (this.picker) return this.picker.seconds ?? 0;

    const numberVal = stringToNumber(this.getAttribute(attributes.SECONDS));

    if (!Number.isNaN(numberVal)) {
      return this.roundPartToInterval(numberVal, this.secondInterval);
    }

    if (this.useCurrentTime && Number.isNaN(numberVal)) {
      const secondsNow = new Date().getSeconds();

      return this.roundPartToInterval(secondsNow, this.secondInterval);
    }

    // Default
    return 0;
  }

  /**
   * Set period attribute and update value in period dropdown
   * @param {string|null} value period param value
   */
  set period(value: string | null) {
    if (value) {
      this.setAttribute(attributes.PERIOD, value);
    } else {
      this.removeAttribute(attributes.PERIOD);
    }

    // Updating hours dropdown with AM/PM range
    if (this.picker) {
      if (this.hasHourRange()) {
        this.picker.hours = String(this.getHourOptions()[0]);
      } else {
        this.picker.period = value;
      }
    }
    if (this.autoupdate && !this.isUpdatingFromInput) this.#setTimeOnField();
  }

  /**
   * period attribute, default is first day period in locale calendar
   * @returns {string} period attribute value
   */
  get period(): string {
    if (this.picker) return this.picker.period ?? '';
    if (!this.picker && !this.hasAttribute(attributes.PERIOD)) return '';

    const attrVal = this.getAttribute(attributes.PERIOD);
    const dayPeriods: Array<string> = this.dayPeriodsWithRange();
    const dayPeriodExists: boolean = dayPeriods.map((item: string) => item.toLowerCase())
      .includes(attrVal?.toString().toLowerCase() as string);

    if (!this.hasPeriod()) return '';

    if (attrVal && dayPeriodExists) {
      return attrVal;
    }

    if (this.useCurrentTime) {
      const hours24Now = new Date().getHours();

      if (hours24Now >= 12) {
        return dayPeriods[1];
      }

      return dayPeriods[0];
    }

    return dayPeriods[0];
  }

  /**
   * Set trigger field/input validation
   * @param {string|null} val validate param
   */
  set validate(val: string | null) {
    if (val) {
      this.setAttribute(attributes.VALIDATE, val);
      this.input?.setAttribute(attributes.VALIDATE, val);
      this.input?.setAttribute(attributes.VALIDATION_EVENTS, this.validationEvents);
      if (typeof this.input?.handleValidation === 'function') {
        this.input?.handleValidation();
      }
    } else {
      this.removeAttribute(attributes.VALIDATE);
      this.input?.removeAttribute(attributes.VALIDATE);
      this.input?.removeAttribute(attributes.VALIDATION_EVENTS);
      if (typeof this.input?.handleValidation === 'function') {
        this.input?.handleValidation();
      }
    }

    this.#setTimeValidation();
  }

  /**
   * validate attribute
   * @returns {string|null} validate param
   */
  get validate(): string | null { return this.getAttribute(attributes.VALIDATE); }

  /**
   * Set which input events to fire validation on
   * @param {string|null} val validation-events attribute
   */
  set validationEvents(val: string | null) {
    if (val) {
      this.setAttribute(attributes.VALIDATION_EVENTS, val);
      this.input?.setAttribute(attributes.VALIDATION_EVENTS, val);
    } else {
      this.removeAttribute(attributes.VALIDATION_EVENTS);
      this.input?.removeAttribute(attributes.VALIDATION_EVENTS);
    }
  }

  /**
   * validation-events attributes
   * @returns {string} validationEvents param. Default is 'change blur'
   */
  get validationEvents(): string { return this.getAttribute(attributes.VALIDATION_EVENTS) ?? 'change blur'; }

  /**
   * Set trigger field tabbable attribute
   * @param {boolean | string} val true or false depending if the trigger field is tabbable
   */
  set tabbable(val: boolean | string) {
    const isTabbable = stringToBool(val);
    if (isTabbable) {
      this.setAttribute(attributes.TABBABLE, String(isTabbable));
      this.input?.setAttribute(attributes.TABBABLE, String(isTabbable));
    } else {
      this.removeAttribute(attributes.TABBABLE);
      this.input?.removeAttribute(attributes.TABBABLE);
    }
  }

  /**
   * Get the trigger field tabbable attribute
   * @returns {boolean | string} tabbable param
   */
  get tabbable(): boolean | string {
    const attrVal = this.getAttribute(attributes.TABBABLE);

    // not tabbable by default
    return attrVal !== null ? stringToBool(attrVal) : false;
  }

  /**
   * Set start of limited hours range
   * @param {string|number|null} val to be set as end-hour attribute
   */
  set startHour(val: number | string | null) {
    if (val) {
      this.setAttribute(attributes.START_HOUR, String(val));
    } else {
      this.removeAttribute(attributes.START_HOUR);
    }

    if (this.picker) this.picker.startHour = val;
  }

  /**
   * start-hour attribute, default is 0
   * @returns {number} startHour param converted to number from attribute value
   */
  get startHour(): number {
    if (this.picker) return this.picker.startHour;

    const numberVal = stringToNumber(this.getAttribute(attributes.START_HOUR));

    if (!Number.isNaN(numberVal) && numberVal >= 0) {
      return numberVal;
    }

    return 0;
  }

  /**
   * Set end of limited hours range
   * @param {string|number|null} val to be set as end-hour attribute
   */
  set endHour(val: number | string | null) {
    if (val) {
      this.setAttribute(attributes.END_HOUR, String(val));
    } else {
      this.removeAttribute(attributes.END_HOUR);
    }

    if (this.picker) this.picker.endHour = val;
  }

  /**
   * end-hour attribute, default is 24
   * @returns {number} endHour param converted to number from attribute value
   */
  get endHour(): number {
    if (this.picker) return this.picker.endHour;

    const numberVal = stringToNumber(this.getAttribute(attributes.END_HOUR));

    if (!Number.isNaN(numberVal) && numberVal <= 24) {
      return numberVal;
    }

    return 24;
  }

  /**
   * Set whether or not to show current time in the dropdowns
   * @param {boolean | string} val useCurrentTime param value
   */
  set useCurrentTime(val: boolean | string) {
    const boolVal = stringToBool(val);

    this.setAttribute(attributes.USE_CURRENT_TIME, boolVal ? 'true' : 'false');

    if (this.picker) this.picker.useCurrentTime = boolVal;
  }

  /**
   * use-current-time attribute
   * @returns {boolean | string} useCurrentTime param converted to boolean from attribute value
   */
  get useCurrentTime(): boolean | string {
    if (this.picker) return this.picker.useCurrentTime;
    const attrVal = this.getAttribute(attributes.USE_CURRENT_TIME);
    return attrVal !== null ? stringToBool(attrVal) : true;
  }

  /**
   * Enable/disable mask for the input
   * @param {boolean | string} val mask param value
   */
  set mask(val: boolean | string) {
    const boolVal = stringToBool(val);

    if (boolVal) {
      this.setAttribute(attributes.MASK, 'true');
      this.input?.setAttribute(attributes.MASK, 'date');
    } else {
      this.removeAttribute(attributes.MASK);
      this.input?.removeAttribute(attributes.MASK);
    }
  }

  /**
   * Get the current mask attribute
   * @returns {boolean} mask param converted to boolean from attribute value
   */
  get mask(): boolean | string {
    const attrVal = this.getAttribute(attributes.MASK);
    return stringToBool(attrVal);
  }

  /**
   * Set the overflow attribute
   * @param {string} value - the overflow setting (ellipsis, etc.)
   */
  set overflow(value: string | null) {
    if (value) {
      this.setAttribute(attributes.OVERFLOW, value);
      this.input?.setAttribute(attributes.OVERFLOW, value);
    } else {
      this.removeAttribute(attributes.OVERFLOW);
      this.input?.removeAttribute(attributes.OVERFLOW);
    }
  }

  /**
   * Get the overflow attribute
   * @returns {string | null} the overflow setting
   */
  get overflow(): string | null {
    return this.getAttribute(attributes.OVERFLOW);
  }

  /**
   * Set the tooltip attribute
   * @param {string | boolean} value - the tooltip setting
   */
  set tooltip(value: string | boolean | null) {
    if (value) {
      this.setAttribute(attributes.TOOLTIP, String(value));
      this.input?.setAttribute(attributes.TOOLTIP, String(value));
    } else {
      this.removeAttribute(attributes.TOOLTIP);
      this.input?.removeAttribute(attributes.TOOLTIP);
    }
  }

  /**
   * Get the tooltip attribute
   * @returns {string | null} the tooltip setting
   */
  get tooltip(): string | null {
    return this.getAttribute(attributes.TOOLTIP);
  }

  /**
   * Gets the trigger button element within the time picker
   * @returns {IdsTriggerButton | null} The IDS trigger button element or null if not found
   */
  get triggerButton() {
    return this.container?.querySelector<IdsTriggerButton>('ids-trigger-button');
  }

  /**
   * Gets the time picker popup element
   * @returns {IdsTimePickerPopup | null} The time picker popup element or null if not found
   */
  get picker() {
    return this.container?.querySelector<IdsTimePickerPopup>('ids-time-picker-popup');
  }
}
